import { type FC, useContext } from "react";
import { useNavigate } from "react-router";
import { ActionButton, CardGrid, CardGridArea, VStack } from "widgets";
import { useURLSearchParam } from "widgets/hooks";

import { WorkflowDetailsCard } from "@/modules/workflow/components/WorkflowDetailsCard.tsx";
import { WorkflowEventPreviewCard, WorkflowPreviewCard } from "@/modules/workflow/components/WorkflowPreviewCard.tsx";
import { useGetActivitiesQuery } from "@/modules/workflow/graphql/workflow.gql.ts";
import { WorkflowContext } from "@/modules/workflow/stores/workflow-context.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";

interface WorkflowCardsProps {
  hideActions?: boolean;
  placeholderMessage?: string;
  previewOnly?: boolean;
  selectedWorkflowId: number | undefined;
}

export const WorkflowCards: FC<WorkflowCardsProps> = (props) => {
  const navigate = useNavigate();
  const { setWorkflow } = useContext(WorkflowContext);
  const sessionId = useURLSearchParam("session");

  // We load the activities here because we don't want to load them for every mount/unmount of the preview
  const { data, error, loading } = useGetActivitiesQuery({
    skip: !props.selectedWorkflowId,
  });

  if (error) {
    throw error;
  }

  const noContent = !((props.selectedWorkflowId && data) || (props.selectedWorkflowId && loading));

  return (
    <>
      {noContent && (
        <ContentPlaceholder style={{ backgroundColor: "var(--page-background)" }}>
          <VStack spacing={20}>
            <div>{props.placeholderMessage || "Please select a workflow to get a preview"}</div>
            {!props.hideActions && (
              <div>
                <ActionButton onPress={() => navigate("/processor")} icon="add_circle" label="Create New Workflow" />
              </div>
            )}
          </VStack>
        </ContentPlaceholder>
      )}
      {props.previewOnly && !noContent && (
        <WorkflowPreviewCard
          workflowId={props.selectedWorkflowId}
          activities={data?.workflow.activityCollector.activities}
          area={CardGridArea.Header}
          updating={loading}
          title=""
          noBorder
          fillHeight
          contentStyle={{}}
        />
      )}
      {!props.previewOnly && (
        <CardGrid style={noContent ? { flex: 0, padding: 0 } : undefined}>
          {sessionId ? (
            <WorkflowEventPreviewCard
              workflowEventId={Number(sessionId)}
              activities={data?.workflow.activityCollector.activities}
              area={CardGridArea.Header}
              updating={loading}
            />
          ) : (
            <WorkflowPreviewCard
              workflowId={props.selectedWorkflowId}
              activities={data?.workflow.activityCollector.activities}
              area={CardGridArea.Header}
              updating={loading}
            />
          )}
          <WorkflowDetailsCard workflowId={props.selectedWorkflowId} area={CardGridArea.Left} onLoad={setWorkflow} />
        </CardGrid>
      )}
    </>
  );
};
